import React from 'react';
import { Box, Grid, Typography, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';

export interface MetricItem {
  id?: string;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'error' | 'info';
}

export interface MetricStripProps {
  metrics: MetricItem[];
  columns?: { xs?: number; sm?: number; md?: number; lg?: number };
}

const toneColor = (tone: MetricItem['tone'], theme: Theme) => {
  switch (tone) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.primary.main;
  }
};

/** Horizontal KPI strip — label, value, optional hint. */
const MetricStrip: React.FC<MetricStripProps> = ({
  metrics,
  columns = { xs: 12, sm: 6, md: 3 },
}) => {
  const theme = useTheme();
  return (
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {metrics.map((m, i) => {
        const accent = toneColor(m.tone, theme);
        return (
          <Grid item key={m.id ?? `${m.label}-${i}`} xs={columns.xs} sm={columns.sm} md={columns.md} lg={columns.lg}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                animation: 'metricLift 0.4s ease-out',
                animationDelay: `${i * 0.04}s`,
                animationFillMode: 'both',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  borderColor: alpha(accent, 0.35),
                  boxShadow: `0 4px 14px ${alpha(accent, 0.08)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  bgcolor: accent,
                },
              }}
            >
              <Typography variant="overline" sx={{ display: 'block', mb: 0.75 }}>
                {m.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {m.value}
              </Typography>
              {m.hint != null && m.hint !== '' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {m.hint}
                </Typography>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MetricStrip;
